import React from 'react';
import {
  SectionWrapper,
  SectionHeader,
  Title,
  Caption,
  MetricSection,
  MetricLabel,
} from './common';
import CollapsibleSection, {
  CollapsibleSectionHeader,
  CollapsibleSectionLabel,
} from '../CollapsibleSection';
import styled from 'styled-components';
import SelfAssessmentTable from './SelfAssessmentTable';
import {
  AssessmentTestSessionDetailResponse,
  AssessmentTestStep,
  AssessmentTestResult,
  AssessmentTestStepAnswer,
} from '../../../api/admin';

export function ResultsSelfAssessment({
  test,
}: {
  test: AssessmentTestSessionDetailResponse | null | undefined;
}) {
  const [isExpanded, setIsExpanded] = React.useState(true);

  const selfAssessmentStep = test?.steps.find(
    (step) => step.label === 'Self Assessment'
  );

  if (!selfAssessmentStep) {
    return null;
  }

  const selfAssessment = {
    step: selfAssessmentStep,
    sessionId: test?.assessmentTestSessionId.toString() ?? '',
    didStartTest: test?.startedAt !== null,
    answer: test?.answers.find((a) => a.id === selfAssessmentStep.id),
    stepReview:
      test?.results?.scenarioEvaluations.find(
        (evaluation) => evaluation.scenarioId === selfAssessmentStep.id
      ) ?? null,
    selfAssessmentCalibration: test?.results?.selfAssessmentCalibration ?? null,
    testDimensions: test?.dimensions ?? [],
  };

  const isStarted = test?.startedAt !== null;

  return (
    <SectionWrapper>
      <SectionHeader>
        <Title>Self Assessment</Title>
        <Caption>
          Candidate's self-evaluation and confidence calibration
        </Caption>
      </SectionHeader>
      <CollapsibleSection
        key={selfAssessment.step.id}
        headerLeft={
          <CollapsibleSectionHeader>
            {selfAssessment.step.title}
          </CollapsibleSectionHeader>
        }
        expanded={isExpanded}
        onToggle={() => setIsExpanded(!isExpanded)}
        headerRight={
          <CollapsibleSectionLabel>
            {selfAssessment.step.label}
          </CollapsibleSectionLabel>
        }
      >
        <StepWrapper>
          <SelfAssessmentSummary {...selfAssessment} isStarted={isStarted} />
          <SelfAssessmentTable
            {...selfAssessment}
            selfAssessmentCalibration={selfAssessment.selfAssessmentCalibration}
          />
        </StepWrapper>
      </CollapsibleSection>
    </SectionWrapper>
  );
}

const StepWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

function SelfAssessmentSummary({
  selfAssessmentCalibration,
  isStarted,
}: {
  step: AssessmentTestStep;
  stepReview: AssessmentTestResult['scenarioEvaluations'][number] | null;
  answer: AssessmentTestStepAnswer | undefined;
  selfAssessmentCalibration:
    | AssessmentTestResult['selfAssessmentCalibration']
    | null;
  isStarted: boolean;
}) {
  if (!isStarted || !selfAssessmentCalibration) {
    return null;
  }

  const overallAlignment = selfAssessmentCalibration.overallAlignment;
  const calibrationSummary = selfAssessmentCalibration.calibrationSummary;

  // Format the alignment label for display
  const formatAlignmentLabel = (alignment: string): string => {
    switch (alignment) {
      case 'well_calibrated':
        return 'Well Calibrated';
      case 'overconfident':
        return 'Overconfident';
      case 'underconfident':
        return 'Underconfident';
      case 'inconsistent':
        return 'Inconsistent';
      default:
        return alignment;
    }
  };

  return (
    <SummaryWrapper>
      <MetricSection>
        <MetricLabel>Calibration</MetricLabel>
        <CalibrationValueWrapper alignment={overallAlignment}>
          <CalibrationValue>
            {formatAlignmentLabel(overallAlignment)}
          </CalibrationValue>
        </CalibrationValueWrapper>
      </MetricSection>
      <MetricSection>
        <MetricLabel>Summary</MetricLabel>
        <SummaryText>{calibrationSummary}</SummaryText>
      </MetricSection>
    </SummaryWrapper>
  );
}

const SummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.bgActive};
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  padding: 24px;
`;

const CalibrationValueWrapper = styled.div<{ alignment: string }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  margin-top: 4px;

  ${({ alignment, theme }) => {
    const lowerAlignment = alignment.toLowerCase();
    switch (lowerAlignment) {
      case 'well_calibrated':
      case 'well calibrated':
      case 'aligned':
        // Aligned -> green (goal state)
        return `
          background-color: #10b98120;
          border: 1px solid #10b98140;
          color: #10b981;
        `;
      case 'overconfident':
        // Overconfident -> amber (attention needed)
        return `
          background-color: #f59e0b20;
          border: 1px solid #f59e0b40;
          color: #f59e0b;
        `;
      case 'underconfident':
        // Underconfident -> amber (attention needed)
        return `
          background-color: #f59e0b20;
          border: 1px solid #f59e0b40;
          color: #f59e0b;
        `;
      case 'inconsistent':
        // Inconsistent -> neutral gray
        return `
          background-color: ${theme.colors.textPrimary}15;
          border: 1px solid ${theme.colors.textPrimary}25;
          color: ${theme.colors.textPrimary}CC;
        `;
      default:
        // Default -> neutral gray
        return `
          background-color: ${theme.colors.textPrimary}15;
          border: 1px solid ${theme.colors.textPrimary}25;
          color: ${theme.colors.textPrimary}CC;
        `;
    }
  }}
`;

const CalibrationValue = styled.div`
  line-height: 1.2;
`;

const SummaryText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.5;
`;
